import { apiFetch } from './api-client';

export interface MLStrategyResponse {
    strategy: string;
    strategyLabel?: string;
    confidence: number;
    regime: string;
    regimeLabel?: string;
    description?: string;
    entry: { min: number; max: number };
    target: { min: number; max: number };
    stopLoss: number;
    riskReward: number;
    riskLevel?: string;
    sectorsToWatch?: Array<{ name: string; sentiment: string }>;
    keyLevels?: { support: number[]; resistance: number[] };
    historicalWinRate?: number;
    modelSource?: 'onnx_live' | 'onnx_bundled' | 'rule_based';
    timestamp?: string;
}

export interface MLRegimeResponse {
    regime: 'trending_bull' | 'trending_bear' | 'sideways' | 'high_volatility' | string;
    confidence: number;
    label?: string;
    emoji?: string;
    reasons?: string[];
    probabilities?: Record<string, number>;
    indicators: Record<string, unknown>;
    modelSource?: 'onnx_live' | 'onnx_bundled' | 'rule_based';
}

export interface MLRadarResponse {
    // Defines the shape for market radar (sector rotation, etc) based on our endpoints
    [key: string]: unknown;
}

/** Fetch 120-dimensional feature vector */
export async function fetchMLFeatures(): Promise<number[]> {
    try {
        const result = await apiFetch<{ features: number[] }>('/ml/features', { cacheTTL: 60 });
        return result.data.features || [];
    } catch (err) {
        console.warn('Failed to fetch ML features:', err);
        return [];
    }
}

/** Fetch strategy prediction */
export async function fetchMLStrategy(): Promise<MLStrategyResponse | null> {
    try {
        const result = await apiFetch<MLStrategyResponse>('/ml/strategy', { cacheTTL: 60 });
        return result.data;
    } catch (err) {
        console.warn('Failed to fetch ML strategy:', err);
        return null;
    }
}

/** Fetch market regime */
export async function fetchMLRegime(): Promise<MLRegimeResponse | null> {
    try {
        const result = await apiFetch<MLRegimeResponse>('/ml/regime', { cacheTTL: 300 }); // regime doesn't change every minute
        return result.data;
    } catch (err) {
        console.warn('Failed to fetch ML regime:', err);
        return null;
    }
}

/** Fetch market radar scores */
export async function fetchMLRadar(): Promise<MLRadarResponse | null> {
    try {
        const result = await apiFetch<MLRadarResponse>('/ml/market-radar', { cacheTTL: 60 });
        return result.data;
    } catch (err) {
        console.warn('Failed to fetch ML market radar:', err);
        return null;
    }
}
